import React, { useCallback, useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { sendEmailOtp } from 'features/authentication/actions';
import {
  ForgotPasswordStep,
  SIGN_IN_PATH,
} from 'features/authentication/SignIn/constants';
import HeaderLayout from 'features/authentication/SignIn/HeaderLayout';
import { ErrorTrackingProps } from 'features/authentication/SignIn/types';

import { toI18n } from 'util/i18n';

import EmailForm from './EmailForm';

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email(toI18n('fe_design_base.errors.field.email_invalid'))
    .required(toI18n('fe_design_base.errors.field.email_required')),
});

interface FormValues {
  email: string;
}

type Props = {
  onSetStep: (view: ForgotPasswordStep) => void;
  onSetEmail: (email: string) => void;
  onSetEmailId: (emailId: string) => void;
} & ErrorTrackingProps;
const EmailFormContainer = ({
  onSetStep,
  onSetEmail,
  onSetEmailId,
  onError,
}: Props) => {
  const [alertError, setAlertError] = useState('');

  const handleError = useCallback(() => {
    const errorMessage = toI18n('fe_design_base.errors.general');
    setAlertError(errorMessage);
    onError(errorMessage);
  }, [onError]);

  const handleSubmit = useCallback(
    ({ email }: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
      onSetEmail(email);
      if (alertError) setAlertError('');

      sendEmailOtp({ email })
        .then(({ email_id }) => {
          onSetEmailId(email_id);
          onSetStep(ForgotPasswordStep.CODE);
        })
        .catch(() => {
          handleError();
          setSubmitting(false);
        });
    },
    [alertError, handleError, onSetEmail, onSetEmailId, onSetStep]
  );

  return (
    <HeaderLayout
      backLinkTo={SIGN_IN_PATH}
      title="authentication.forgot_password.email.title"
      description="authentication.forgot_password.email.description"
      dataTestId="EmailForm"
      alertError={alertError}
    >
      <Formik
        onSubmit={handleSubmit}
        initialValues={{ email: '' }}
        validationSchema={validationSchema}
      >
        {({ dirty, isValid, isSubmitting, setFieldValue }) => (
          <EmailForm
            dirty={dirty}
            isValid={isValid}
            isSubmitting={isSubmitting}
            setFieldValue={setFieldValue}
          />
        )}
      </Formik>
    </HeaderLayout>
  );
};

export default EmailFormContainer;
